import React, { useEffect, useMemo } from "react";
import { useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useAtomValue, useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";

import { ApplicationScreenLayout } from "@/blocks/ApplicationScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnCard, VnCardTitle } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnRadioGroupInput } from "@/components/VnRadioGroupInput/VnRadioGroupInput";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateCurrentPatientMutation } from "@/data/mutations/patientsMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  memberChildCaregiverStatusFormSchema,
  MemberChildCaregiverStatusFormSchemaType,
} from "@/domain/applications";
import { HouseholdMemberType } from "@/domain/householdMember";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { getChildCaregiverStatusOptions } from "@/utils/applications";
import { formatHouseholdMemberName } from "@/utils/householdMember";

type MemberCaregiverStatusCardPropsType = {
  member: HouseholdMemberType;
};

const MemberCaregiverStatusCard: React.FC<
  MemberCaregiverStatusCardPropsType
> = ({ member }) => {
  const form = useForm<MemberChildCaregiverStatusFormSchemaType>({
    defaultValues: {
      childCaregiverStatus: member.childCaregiverStatus,
    },
    resolver: standardSchemaResolver(memberChildCaregiverStatusFormSchema),
  });

  const { mutate: updateHouseholdMember } = useUpdateHouseholdMemberMutation();

  // Auto-save when child caregiver status changes
  useEffect(() => {
    const subscription = form.watch((formData, { name }) => {
      if (name === "childCaregiverStatus" && formData.childCaregiverStatus) {
        updateHouseholdMember({
          id: member.id,
          householdMemberData: {
            childCaregiverStatus: formData.childCaregiverStatus,
          },
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [form, member.id, updateHouseholdMember]);

  return (
    <VnForm form={form} isInputOnlyForm>
      <VnCard className="flex-row items-center justify-between p-4">
        <VnCardTitle variant="lgMedium">
          {formatHouseholdMemberName(member)}
        </VnCardTitle>

        <VnRadioGroupInput
          name="childCaregiverStatus"
          control={form.control}
          options={getChildCaregiverStatusOptions()}
          radioGroupClassName="gap-4 p-4 flex flex-row"
          radioItemClassName="bg-card"
        />
      </VnCard>
    </VnForm>
  );
};

export const ApplicationChildCaregiverStatusScreen = () => {
  const router = useRouter();
  const setApplicationCurrentStep = useSetAtom(applicationCurrentStepAtom);
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);
  const { mutate: updateCurrentPatient } = useUpdateCurrentPatientMutation();

  const { data: householdMembers } = useListHouseholdMembersQuery();

  const isNextDisabled = useMemo(() => {
    if (!householdMembers) return true;
    return householdMembers.some(member => !member.childCaregiverStatus);
  }, [householdMembers]);

  const handleNext = () => {
    if (isNextDisabled) {
      return;
    }

    updateCurrentPatient(
      {
        patientData: {
          applicationLastStep: "CHILD_CAREGIVER_STATUS",
        },
      },
      {
        onSuccess: () => {
          setApplicationCurrentStep(step =>
            Math.min(step + 1, applicationTotalSteps),
          );

          router.navigate({
            pathname: "/(authed)/(modal)/application/other-statuses",
          });
        },
      },
    );
  };

  return (
    <ApplicationScreenLayout
      onNextButtonPress={handleNext}
      isNextButtonDisabled={isNextDisabled}
      titleI18nKey="native:applicationChildCaregiverStatusScreen.title"
      descriptionI18nKey="native:applicationChildCaregiverStatusScreen.description">
      <VnBox className="gap-4">
        {householdMembers?.map(member => {
          return <MemberCaregiverStatusCard key={member.id} member={member} />;
        })}
      </VnBox>
    </ApplicationScreenLayout>
  );
};
